import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Magnet, Check, ArrowLeft, BarChart3, Infinity, Crown, Headphones, Loader2, ShoppingCart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPlan } from "@/hooks/useUserPlan";

export default function Upgrade() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [proLeadLimit, setProLeadLimit] = useState<number | null>(null);
  const [looseLeadsPrice, setLooseLeadsPrice] = useState<number | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role, isLoading: isPlanLoading } = useUserPlan();

  useEffect(() => {
    if (searchParams.get("canceled") === "true") {
      toast({
        title: "Pagamento cancelado",
        description: "Você pode tentar novamente quando quiser.",
        variant: "destructive",
      });
    }
  }, [searchParams, toast]);

  useEffect(() => {
    const loadSettings = async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .in("key", ["pro_lead_limit", "loose_leads_price_brl"]);
      
      data?.forEach((setting) => {
        if (setting.key === "pro_lead_limit") {
          setProLeadLimit(Number(setting.value));
        } else if (setting.key === "loose_leads_price_brl") {
          setLooseLeadsPrice(Number(setting.value));
        }
      });
    };
    loadSettings();
  }, []);

  const features = [
    { icon: Infinity, text: proLeadLimit ? `${proLeadLimit} leads inclusos por mês` : "Leads inclusos por mês" },
    { icon: BarChart3, text: "Pixel do Google Analytics" },
    { icon: BarChart3, text: "Pixel do Meta (Facebook)" },
    { icon: Headphones, text: "Suporte prioritário" },
  ];

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Faça login primeiro",
          description: "Você precisa estar logado para fazer upgrade.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("Não foi possível criar a sessão de checkout");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Erro ao processar",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyLeads = async () => {
    setIsLoadingLeads(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Faça login primeiro",
          description: "Você precisa estar logado para comprar leads.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke("buy-leads", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("Não foi possível criar a sessão de checkout");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Erro ao processar",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const isPro = role === "pro" || role === "admin";

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center shadow-glow">
              <Magnet className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-xl font-display font-bold">Magnet Link</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-16 max-w-2xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-primary mb-6">
            <Crown className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-4xl font-display font-bold mb-4">
            Faça Upgrade para o Plano Pro
          </h1>
          <p className="text-xl text-muted-foreground">
            Por menos de R$1 por dia, capture leads ilimitados
            <br />
            com perguntas inteligentes geradas por IA.
          </p>
        </div>

        <Card className="border-2 border-primary">
          <CardHeader className="text-center pb-2">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-2">
              <Crown className="h-4 w-4" />
              Mais popular
            </div>
            <CardTitle className="text-3xl font-display">Plano Pro</CardTitle>
            <CardDescription>
              Tudo que você precisa para crescer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center">
              <span className="text-5xl font-bold">R$ 29,90</span>
              <span className="text-muted-foreground">/mês</span>
            </div>

            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="h-3 w-3 text-primary" />
                  </div>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>

            <Button 
              onClick={handleUpgrade}
              disabled={isLoading}
              className="w-full bg-gradient-primary hover:opacity-90 text-lg py-6"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Crown className="mr-2 h-5 w-5" />
                  Fazer Upgrade Agora
                </>
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Cancele quando quiser • Sem compromisso
            </p>
          </CardContent>
        </Card>

        {/* Card de Leads Avulsos */}
        <Card className="mt-8">
          <CardHeader className="text-center pb-2">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
              <ShoppingCart className="h-4 w-4" />
              Precisa de mais leads?
            </div>
            <CardTitle className="text-2xl font-display">100 Leads Avulsos</CardTitle>
            <CardDescription>
              Compre leads extras sem precisar de assinatura
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <span className="text-4xl font-bold">
                R$ {looseLeadsPrice ? (looseLeadsPrice / 100).toFixed(2).replace(".", ",") : "10,00"}
              </span>
              <span className="text-muted-foreground"> / 100 leads</span>
            </div>

            <Button 
              onClick={handleBuyLeads}
              disabled={isLoadingLeads}
              variant="outline"
              className="w-full text-lg py-6"
            >
              {isLoadingLeads ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Comprar Leads Avulsos
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          {!isPro && (
            <p className="text-muted-foreground mb-4">
              Seus formulários continuam funcionando e capturando leads.
              <br />
              O upgrade libera as perguntas por IA e exportação de dados.
            </p>
          )}
          <Button variant="ghost" asChild>
            <Link to="/dashboard">Voltar para o Dashboard</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}